var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { SmartAuthErrorReason, SmartAuthProvider, SmartAuthState, SmartAuthType } from "../Logic/SmartAuthManager";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
const t = getObservableT("patients");
export class PatientConsentReauthorizationViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.reauthType = null;
        this.authManager = container.authManager;
        this.smartAuthManager = container.smartAuthManager;
        this.smartAuthWindow = container.smartAuthWindow;
        this.smartAuthManager.resetAuth();
    }
    get isPreview() {
        return this.patientConsentUIManager.isInteractivePreview;
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
    get isScreenshotPreviewMode() {
        return this.patientConsentUIManager.isScreenshotPreviewMode;
    }
    async reauth() {
        if (this.isPreview || this.isScreenshotMode)
            return;
        if (!this.authManager.orgId)
            return;
        const provider = this.authManager.gfidProvider === GFIDProvider.CLEAR ? SmartAuthProvider.CLEAR_IAL2 : SmartAuthProvider.EPIC;
        const smartAuthType = this.reauthType === "parentGuardian" || this.reauthType === "additionalParentGuardian"
            ? SmartAuthType.PROXY_ACCESS_REAUTH
            : SmartAuthType.PATIENT_REAUTH;
        await this.smartAuthWindow.createSmartAuthWindow(provider, smartAuthType, this.authManager.orgId);
    }
    get smartAuthState() {
        return this.smartAuthManager.state;
    }
    get errorReason() {
        return this.smartAuthManager.errorReason;
    }
    get loading() {
        return this.smartAuthState === SmartAuthState.IN_PROGRESS || this.smartAuthState === SmartAuthState.LOGGING_IN;
    }
    get reauthSuccess() {
        var _a;
        if (this.isScreenshotMode && !this.isScreenshotPreviewMode)
            return true;
        const reauthSuccess = (_a = this.smartAuthManager.reauthSuccess) !== null && _a !== void 0 ? _a : false;
        if (this.reauthType === "parentGuardian") {
            return reauthSuccess && this.patientConsentUIManager.session.parentGuardianProxyAccess !== null;
        }
        else if (this.reauthType === "additionalParentGuardian") {
            return reauthSuccess && this.patientConsentUIManager.session.additionalParentGuardianProxyAccess !== null;
        }
        else {
            return reauthSuccess;
        }
    }
    get continueButtonEnabled() {
        if (this.isPreview || this.isScreenshotMode)
            return true;
        return this.reauthSuccess;
    }
    get errorText() {
        var _a, _b;
        if (this.reauthType === "additionalParentGuardian" &&
            this.patientConsentUIManager.session.isAdditionalParentGuardianNameUnique === false) {
            return t("The account you logged into was already used to sign. The second signature must come from a different account that also has access to {{ patientName }}'s account. Please try again. If you need help, click the Help icon at the top.", {
                patientName: this.patientName,
            });
        }
        if (this.reauthSuccess || this.errorReason === null || this.smartAuthState !== SmartAuthState.ERROR)
            return null;
        if (this.errorReason === SmartAuthErrorReason.LOGIN_WINDOW_CLOSED)
            return null;
        if (this.errorReason === SmartAuthErrorReason.AUTH_FAILED) {
            if (this.reauthType === "parentGuardian" || this.reauthType === "additionalParentGuardian") {
                return t("The account you logged in to doesn’t have access to {{ patientName }}’s account. Please double-check that you logged into an account that has proxy access to their account. If you need help, click the Help icon at the top.", { patientName: this.patientName });
            }
            else {
                return t("The account you logged in to doesn’t match the person who was sent this request. Please double-check your login details and try again. If you need support, click the Help icon at the top.");
            }
        }
        else {
            if (this.reauthType === "parentGuardian" || this.reauthType === "additionalParentGuardian") {
                return t("We’re unable to connect to {{ patientPortalName }} at the moment. We've saved your progress so far, but due to a technical issue on our end, we can't connect your account. Please try logging in again. If the issue keeps happening, click the Help icon at the top for support.", { patientPortalName: (_a = this.patientPortalName) !== null && _a !== void 0 ? _a : "your patient portal" });
            }
            else {
                return t("We’re unable to connect to {{ patientPortalName }} at the moment", {
                    patientPortalName: (_b = this.patientPortalName) !== null && _b !== void 0 ? _b : "your patient portal",
                });
            }
        }
    }
    async continue() {
        if (!this.continueButtonEnabled)
            return;
        this.goToNextPage();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentReauthorizationViewModel.prototype, "reauthType", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "isPreview", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "isScreenshotMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "isScreenshotPreviewMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "smartAuthState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "errorReason", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "loading", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "reauthSuccess", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "continueButtonEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentReauthorizationViewModel.prototype, "errorText", null);
